import { Add, Close, Remove } from '@material-ui/icons';
import { isEventAddOnAvailable } from 'types/shared/helper/eventAddOns';
import useToast from 'CustomHooks/useToast';
import { Severity } from 'Contexts/RestaurantContext';
import Box from 'Components/Atoms/Box';
import Checkbox from 'Components/Atoms/Checkbox';
import IconButton from 'Components/Atoms/IconButton';
import SwitchBox from 'Components/Atoms/Switch';
import TextField from 'Components/Atoms/TextField';

import Typography from 'Components/Atoms/Typography';
import DateDropdown from 'Components/Organisms/DateDropdown';
import { ReservationContext } from 'Contexts/ReservationContext';
import useAvailableTimes from 'CustomHooks/useGetAvailableTimes';
import useRestaurant from 'CustomHooks/useRestaurant';
import BookingWindowNotice from 'App/ReservationBook/Components/BookingWindowNotice';
import usePinAuth from 'CustomHooks/usePinAuth';
import { ActivityTarget } from 'utils/securityActivity/events';
import React, { useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { EditReservation, TimeSlot } from 'types/reservations';
import { Event } from 'types/shared/documents/restaurants/event';
import { noop, toCurrencyString } from 'utils/helper';
import {
  getDefaultTicketSelection,
  getMinimumAddOnCount,
  getOptionTitle,
  getRemainingOptionCount,
  getRequiredTicketOptionCount,
  getTicketPaymentSummary,
  getTicketSelectionError,
  getWholeGroupAddOnCount,
  normalizeTicketSelection,
  sortTicketOptions,
  updateTicketSelection,
} from 'utils/ticketSession';
import GuestsDropdown from '../../GuestsDropdown';
import ReservationLength from '../../ReservationLength';
import TableDropdown from '../../TableDropdown';
import TimeDropdown from '../../TimeDropdown';
import OccassionDropdown from './OccassionDropdown';
import TableStrDropdown from './TableStrDropdown';
import CustomElements from './CustomElements';

type FirstStepProps = {
  reservation: EditReservation;
  onReservationChange: React.Dispatch<
    React.SetStateAction<EditReservation | null>
  >;
  isNew?: boolean;
  settings?: {
    hideResLength: boolean;
    hideOccassion: boolean;
  };
  occassions: { id: string; title: string; hidden?: boolean }[];
  restaurantId: string;
  uid: string;
  lightReservation?: boolean;
  currentTable?: string;
  setCurrentTable?: React.Dispatch<React.SetStateAction<string>>;
};

type reservationFields =
  | 'date'
  | 'occassion'
  | 'guests'
  | 'reservationLength'
  | 'timeSlot'
  | 'tables'
  | 'tableStr'
  | 'sendConfirmationEmail'
  | 'ticketMode'
  | 'ticket';

type newValue =
  | string
  | string[]
  | number
  | EditReservation['ticket']
  | EditReservation['ticketMode']
  | undefined
  | (TimeSlot & { protectedTime?: boolean })
  | boolean;

const getEventTitle = (event: Event) =>
  event.title?.de || event.title?.en || 'Event';

type TicketOptionSelectorProps = {
  event: Event;
  reservation: EditReservation;
  onReservationChange: React.Dispatch<
    React.SetStateAction<EditReservation | null>
  >;
};

const TicketOptionSelector = ({
  event,
  reservation,
  onReservationChange,
}: TicketOptionSelectorProps) => {
  const [countDrafts, setCountDrafts] = useState<Record<string, string>>({});

  const updateCount = (
    field: 'options' | 'optionalAddOns',
    id: string,
    next: number,
  ) => {
    onReservationChange((old) => {
      if (!old) return null;

      return {
        ...old,
        ticket: updateTicketSelection({
          event,
          guests: old.guests ?? 0,
          ticket: old.ticket,
          field,
          id,
          next,
          preservedAddOns: old.ticketPaidAddOns,
        }),
      };
    });
  };

  const getDraftKey = (field: 'options' | 'optionalAddOns', id: string) =>
    `${field}:${id}`;

  const isValidCount = (rawValue: string, min: number, max: number | null) => {
    if (!/^\d+$/.test(rawValue.trim())) return false;

    const next = Number(rawValue);

    return (
      Number.isInteger(next) &&
      next >= min &&
      (max === null || next <= max)
    );
  };

  const renderOption = (
    option: NonNullable<Event['options']>[number],
    field: 'options' | 'optionalAddOns',
  ) => {
    const value = reservation.ticket?.[field]?.[option.id] ?? 0;
    const remaining = getRemainingOptionCount(event, field, option);
    const wholeGroupCount =
      field === 'optionalAddOns'
        ? getWholeGroupAddOnCount(option, reservation.guests ?? 0)
        : null;
    const displayValue = wholeGroupCount !== null && value > 0 ? 1 : value;
    const selectedTicketOptions = Object.values(
      reservation.ticket?.options ?? {}
    ).reduce((sum, count) => sum + Math.max(0, Math.floor(Number(count) || 0)), 0);
    const maxTicketOptions = getRequiredTicketOptionCount(
      event,
      reservation.guests ?? 0
    );
    const otherSelectedTicketOptions =
      field === 'options' ? selectedTicketOptions - value : 0;
    const maxByGuestCount =
      field === 'options'
        ? Math.max(0, maxTicketOptions - otherSelectedTicketOptions)
        : Number.POSITIVE_INFINITY;
    const maxAllowed = Math.min(remaining, maxByGuestCount);
    const min =
      field === 'optionalAddOns'
        ? getMinimumAddOnCount(option, reservation.guests ?? 0)
        : 0;
    const displayMin = wholeGroupCount !== null && min > 0 ? 1 : min;
    const displayMaxAllowed =
      wholeGroupCount !== null
        ? maxAllowed >= wholeGroupCount
          ? 1
          : 0
        : maxAllowed;
    const draftKey = getDraftKey(field, option.id);
    const draftValue = countDrafts[draftKey];
    const inputValue = draftValue ?? String(displayValue);
    const inputInvalid = !isValidCount(inputValue, displayMin, displayMaxAllowed);
    const increaseDisabled = displayValue >= displayMaxAllowed;
    const decreaseDisabled = displayValue <= displayMin;
    const toStoredCount = (count: number) =>
      wholeGroupCount !== null ? (count > 0 ? wholeGroupCount : 0) : count;
    const handleCountInputChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const nextRawValue = event.target.value;

      setCountDrafts((old) => ({
        ...old,
        [draftKey]: nextRawValue,
      }));

      if (isValidCount(nextRawValue, displayMin, displayMaxAllowed)) {
        updateCount(field, option.id, toStoredCount(Number(nextRawValue)));
      }
    };
    const setButtonCount = (next: number) => {
      updateCount(field, option.id, toStoredCount(next));
      setCountDrafts((old) => ({
        ...old,
        [draftKey]: String(next),
      }));
    };

    return (
      <Box
        key={`${field}-${option.id}`}
        className="ticket-option-row"
      >
        <Box className="ticket-option-copy">
          <Typography variant="text-3" block className="ticket-option-title">
            {getOptionTitle(option)}
          </Typography>
          <Typography
            variant="text-4"
            color="subdued"
            block
            className="ticket-option-price"
          >
            {toCurrencyString(
              (field === 'options'
                ? (event.amount ?? 0) + (option.amount ?? 0)
                : option.amount ?? 0) || 0,
              '€',
              false,
              1
            )}
            {Number.isFinite(remaining) ? ` • ${remaining} verfügbar` : ''}
            {wholeGroupCount !== null ? ' • Ganze Gruppe' : ''}
            {min && wholeGroupCount === null ? ` • Min. ${min}` : ''}
          </Typography>
        </Box>
        <Box className="ticket-option-controls">
          <IconButton
            size="small"
            className="ticket-option-action"
            aria-label="Auswahl reduzieren"
            title="Auswahl reduzieren"
            disabled={decreaseDisabled}
            onClick={() => setButtonCount(displayValue - 1)}
          >
            <Remove />
          </IconButton>
          <input
            className={[
              'ticket-option-count-input',
              inputInvalid ? 'ticket-option-count-input--invalid' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            inputMode="numeric"
            pattern="[0-9]*"
            value={inputValue}
            aria-label="Auswahlmenge"
            onChange={handleCountInputChange}
            onBlur={() => {
              if (inputInvalid) {
                setCountDrafts((old) => ({
                  ...old,
                  [draftKey]: String(value),
                }));
              }
            }}
          />
          <IconButton
            size="small"
            className="ticket-option-action"
            aria-label="Auswahl erhöhen"
            title="Auswahl erhöhen"
            disabled={increaseDisabled}
            onClick={() => setButtonCount(displayValue + 1)}
          >
            <Add />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box className="ticket-selector">
      {!!event.options?.length && (
        <>
          <Typography variant="text-4" color="subdued" translation="reservations">
            Ticket options
          </Typography>
          {sortTicketOptions(event.options).map((option) =>
            renderOption(option, 'options')
          )}
        </>
      )}
      {!!event.optionalAddOns?.length && (
        <Box style={{ marginTop: 12 }}>
          <Typography variant="text-4" color="subdued" translation="reservations">
            Additional options
          </Typography>
          {sortTicketOptions(event.optionalAddOns).filter(option => isEventAddOnAvailable(option, reservation.guests ?? 0)).map((option) =>
            renderOption(option, 'optionalAddOns'),
          )}
        </Box>
      )}
    </Box>
  );
};

// @TODO ERRORS LOADING AND MESSAGE

// @TODO Time Warnings

const FirstStep = ({
  reservation,
  onReservationChange = noop,
  isNew = false,
  settings,
  restaurantId,
  occassions,
  uid,
  lightReservation = false,
  currentTable,
  setCurrentTable,
}: FirstStepProps) => {
  const { t } = useTranslation('reservations');
  const { availableTimes, events, reservationLength, message, waitinglist, bookingWindow } =
    useAvailableTimes(restaurantId, reservation, onReservationChange, uid);

  const { authenticate } = usePinAuth();
  // Overrides in this form name the booking (or its hold) they were made for.
  const activityTarget: ActivityTarget | undefined = reservation.reservationId
    ? { type: 'reservation', id: reservation.reservationId }
    : undefined;

  const { reservationSettings } = useRestaurant();

  const {
    tableStrs: tableStrOptions,
    setStorageTableStrs,
    setShowTimesInEdit,
    showTimesInEditMode,
    hasWaitingList,
    shifts,
  } = useContext(ReservationContext);

  const [addMode, setAddMode] = useState<boolean>(false);
  const toast = useToast();
  const selectedEvent = reservation.event ?? null;
  const ticketMode = reservation.ticketMode ?? 'reservation_only';
  const ticketModeLocked = !!reservation.ticketId;
  const ticketSelectionError = getTicketSelectionError(
    selectedEvent,
    reservation.guests ?? 0,
    reservation.ticket,
    reservation.ticketPaidAddOns
  );
  const ticketPaymentSummary =
    selectedEvent && ticketMode !== 'reservation_only'
      ? getTicketPaymentSummary({
          event: selectedEvent,
          guests: reservation.guests ?? 0,
          ticket: reservation.ticket,
          preservedAddOns: reservation.ticketPaidAddOns,
          coupon: reservation.ticketCoupon,
          voucher: reservation.ticketVoucher,
          partialPayment: 1,
          includeServiceFee: false,
        })
      : null;
  const ticketOriginalAmount =
    typeof reservation.ticketOriginalAmount === 'number'
      ? reservation.ticketOriginalAmount
      : null;
  const ticketOriginalAdjustment =
    (reservation.ticketOriginalCouponAmount ?? 0) +
    (reservation.ticketOriginalVoucherAmount ?? 0);
  const ticketComparableAmount = ticketPaymentSummary
    ? Math.max(ticketPaymentSummary.ticketTotal - ticketOriginalAdjustment, 0)
    : 0;
  const ticketPriceDelta =
    ticketPaymentSummary && ticketOriginalAmount !== null
      ? Number((ticketComparableAmount - ticketOriginalAmount).toFixed(2))
      : 0;
  const hasTicketPriceDelta = Math.abs(ticketPriceDelta) > 0.009;

  const handleChange = (
    newValue: newValue,
    type: reservationFields | 'hostComment',
  ) => {
    if (type === 'guests' && Number(newValue) !== reservation.ticketOriginalGuests &&
        Number(newValue) < (reservation.ticketPaidMinGuests ?? 0)) {
      toast('Bitte nutze für bezahlte Zusatzleistungen den bestehenden Änderungs- oder Stornoweg.', Severity.WARNING);
      return;
    }
    const newReservation: EditReservation = {
      ...reservation,
      changedTablePerHandBefore: false,
    };

    // console.log({
    //   newReservation,
    // });

    if (type === 'hostComment') {
      if (newReservation.guest) {
        newReservation.guest.hostComment = newValue as string;
      } else {
        newReservation.guest = {
          name: 'Walkin ' + (newReservation?.tables?.join(', ') ?? ''),
          hostComment: newValue as string,
          phone: '',
          email: '',
        };
      }

      return onReservationChange(newReservation);
    } else if (type === 'ticketMode') {
      if (reservation.ticketId) return;

      const mode = newValue as EditReservation['ticketMode'];

      newReservation.ticketMode = mode;

      if (mode !== 'reservation_only' && selectedEvent) {
        newReservation.ticket =
          newReservation.ticket ??
          getDefaultTicketSelection(selectedEvent, newReservation.guests ?? 0);
        newReservation.ticketSendVia = newReservation.ticketSendVia ?? 'email';
        newReservation.ticketValidTillMinutes =
          newReservation.ticketValidTillMinutes ?? 120;
        newReservation.ticketValidTillValue =
          newReservation.ticketValidTillValue ?? 2;
        newReservation.ticketValidTillUnit =
          newReservation.ticketValidTillUnit ?? 'hours';
        newReservation.ticketPartialPayment =
          newReservation.ticketPartialPayment ?? 1;
        newReservation.ticketPartialPaymentMode =
          newReservation.ticketPartialPaymentMode ?? 'percentage';
        newReservation.ticketCanEdit = newReservation.ticketCanEdit ?? true;
      }
    } else {
      newReservation[type] = newValue as Exclude<
        keyof EditReservation,
        newValue
      >;
    }

    if (type === 'timeSlot') {
      const nextTimeSlot = newValue as TimeSlot;
      const event =
        nextTimeSlot?.eventId && events?.length
          ? events.find((e) => e.id === nextTimeSlot.eventId) ?? null
          : null;

      if (newReservation.ticketId) {
        newReservation.event = event ?? newReservation.event;

        if (event && newReservation.ticket) {
          newReservation.ticket = normalizeTicketSelection(
            event,
            newReservation.guests ?? 0,
            newReservation.ticket,
            newReservation.ticketPaidAddOns
          );
        }
      } else {
        newReservation.event = event;

        if (event) {
          const currentTicket =
            selectedEvent?.id === event.id ? newReservation.ticket : undefined;

          newReservation.ticketMode =
            newReservation.ticketMode ?? 'reservation_only';
          newReservation.ticket = currentTicket
            ? normalizeTicketSelection(event, newReservation.guests ?? 0, currentTicket)
            : getDefaultTicketSelection(event, newReservation.guests ?? 0);
        } else {
          newReservation.ticketMode = 'reservation_only';
          newReservation.ticket = undefined;
          newReservation.ticketCouponCode = undefined;
          newReservation.ticketCoupon = null;
          newReservation.ticketVoucherCode = undefined;
          newReservation.ticketVoucher = null;
        }
      }
    }

    if (type === 'guests' && newReservation.event && newReservation.ticket) {
      newReservation.ticket = normalizeTicketSelection(
        newReservation.event,
        Number(newValue) || 0,
        newReservation.ticket,
        newReservation.ticketPaidAddOns
      );
    }

    console.log('old resa', reservation);
    console.log('new resa', newReservation);

    if (
      type === 'timeSlot' &&
      typeof newValue === 'object' &&
      !Array.isArray(newValue) &&
      !newReservation?.tables?.length &&
      !reservationSettings?.manualTableAssignment
    ) {
        newReservation.tables = newValue?.combinations?.[0]?.tables || [];
        newReservation.space = newValue?.combinations?.[0]?.space;
      }

    if (
      (newValue as any)?.protectedTime &&
      (newValue as any)?.waitinglist &&
      !newReservation.addToWaitinglist
    ) {
      newReservation.addToWaitinglist = { time: (newValue as any)?.time ?? '' };
    }

    if (type === 'timeSlot' && newReservation.addToWaitinglist) {
      if (!(newValue as TimeSlot).waitinglist) {
        newReservation.addToWaitinglist = null;
      } else {
        newReservation.addToWaitinglist.time = (newValue as TimeSlot).time;
      }
    }

    if (type === 'tables' && Array.isArray(newValue)) {
      newReservation.space = reservation?.timeSlot?.combinations.find(
        (c) =>
          JSON.stringify(c.tables.sort()) === JSON.stringify(newValue.sort()),
      )?.space;

      newReservation.tableStr = newValue.toString();
    }

    if (type === 'reservationLength' && newReservation.timeSlot) {
      console.log('here!');
      if (newReservation.walkin)
        newReservation.reservationLength = newValue as number;
      newReservation.timeSlot.reservationLength = newValue as number;
    }
    console.log('very new', newReservation);

    if (type === 'tableStr') {
      newReservation.tableStr = newValue?.toString();
      if (setCurrentTable) setCurrentTable(newValue?.toString() ?? '');
      // closeAddMode()
    }

    if (type === 'sendConfirmationEmail') {
      newReservation.sendConfirmationEmail = !!newValue;

      if (newReservation.guest) {
        newReservation.guest.sendEmail = !!newValue;
      }
    }
    console.log({ newReservation });
    onReservationChange(newReservation);
  };

  const openAddMode = () => setAddMode(true);
  const closeAddMode = () => setAddMode(false);

  console.log('tableStrs', { tableStrOptions });

  return (
    <Box className="new-reservation-sidebar ">
      <Box flex className="flex-column" style={{ textAlign: 'center' }}>
        {(!isNew || (isNew && !reservation.walkin)) && (
          <>
            <DateDropdown
              value={reservation.date}
              onChange={(newDate) => handleChange(newDate, 'date')}
            />
            {!reservation.waitinglistEntry && (
              <Typography textAlign="center" variant="text-4" color="subdued">
                Tab to open
              </Typography>
            )}
          </>
        )}
      </Box>

      {!!reservation.waitinglistEntry && (
        <>
          <Typography
            textAlign="center"
            variant="text-4"
            block
            translation="reservations"
          >
            Make Sure to contact the Guest. Currently we do not contact them
            automatically
          </Typography>
          <Typography
            textAlign="center"
            variant="text-3"
            color="secondary"
            block
            href={`tel:${reservation.waitinglistEntry.guest.phone}`}
          >
            {reservation.waitinglistEntry.guest.phone}
          </Typography>
        </>
      )}

      <Box className="first-step-field">
        <OccassionDropdown
          isHidden={settings?.hideOccassion || occassions.length === 1}
          occassions={occassions}
          value={reservation.occassion}
          onChange={(newValue) => handleChange(newValue, 'occassion')}
          date={reservation.date}
          shifts={shifts}
        />
      </Box>

      <Box className="first-step-field">
        <Typography variant="text-3" translation="reservations">
          <Trans i18nKey="reservations_Number of Guests">
            Number of Guests
          </Trans>
          <br />{' '}
          {!!reservation.waitinglistEntry && (
            <small>
              <Trans i18nKey="reservations_Requested">Angefragt</Trans>:{' '}
              {reservation.waitinglistEntry.guests}
            </small>
          )}
        </Typography>
        <GuestsDropdown
          value={reservation.guests}
          onChange={(newValue) => handleChange(newValue, 'guests')}
          activityTarget={activityTarget}
        />
      </Box>

      {isNew &&
        !reservation.walkin &&
        !!reservation.date &&
        !!reservation.occassion &&
        !!reservation.guests && (
          <CustomElements
            id={`${encodeURIComponent(restaurantId)}/waitinglist?${new URLSearchParams({
              date: reservation.date,
              time: reservation.timeSlot?.time ?? reservation.time ?? '00:00',
              guests: String(reservation.guests),
              occassion: reservation.occassion,
              source: 'inHouse',
            }).toString()}`}
            customData={reservation.customData ?? {}}
            firstStepOnly
            onCustomDataUpdate={(customData, complete) => {
              onReservationChange((current) =>
                current === null ? null : {
                  ...current,
                  customData: complete
                    ? { ...customData, ...(current.customData ?? {}) }
                    : { ...(current.customData ?? {}), ...customData },
                }
              );
            }}
          />
        )}

      <Box className="first-step-field">
        <TimeDropdown
          value={reservation.timeSlot}
          waitinglistEntry={reservation.waitinglistEntry}
          onChange={(newValue) => handleChange(newValue, 'timeSlot')}
          timeSlots={availableTimes}
          lightReservation={lightReservation}
          timeSlotError={message}
          date={reservation.date}
          waitinglist={waitinglist}
          activityTarget={activityTarget}
        />
        <BookingWindowNotice bookingWindow={bookingWindow} />
      </Box>

      {!!selectedEvent && (
        <Box
          className="first-step-field"
          style={{
            borderTop: '1px solid var(--color-border)',
            paddingTop: 12,
          }}
        >
          <Typography variant="text-3" block>
            {getEventTitle(selectedEvent)}
          </Typography>
          <Typography
            variant="text-4"
            color="subdued"
            block
            translation="reservations"
          >
            This slot has an event connected.
          </Typography>
          {ticketModeLocked ? (
            <Box className="ticket-locked-mode">
              <Typography variant="text-4" translation="reservations" block>
                Ticket mode cannot be changed for existing tickets.
              </Typography>
              <Typography variant="text-3" translation="reservations" block>
                {ticketMode === 'pay_at_restaurant'
                  ? 'Payment at restaurant'
                  : 'Payment Link'}
              </Typography>
            </Box>
          ) : (
            <Box style={{ marginTop: 8 }}>
              <Checkbox
                radio
                checked={ticketMode === 'reservation_only'}
                onChange={() => handleChange('reservation_only', 'ticketMode')}
                label="Reservation only"
                translation="reservations"
                style={{ marginLeft: -12 }}
              />
              <Checkbox
                radio
                checked={ticketMode === 'pay_at_restaurant'}
                onChange={() => handleChange('pay_at_restaurant', 'ticketMode')}
                label="Without Payment / Payment at Restaurant"
                translation="reservations"
                style={{ marginLeft: -12 }}
              />
              <Checkbox
                radio
                checked={ticketMode === 'payment_link'}
                onChange={() => handleChange('payment_link', 'ticketMode')}
                label="Payment Link"
                translation="reservations"
                style={{ marginLeft: -12 }}
              />
            </Box>
          )}
          {ticketMode !== 'reservation_only' && (
            <>
              <TicketOptionSelector
                event={selectedEvent}
                reservation={reservation}
                onReservationChange={onReservationChange}
              />
              {ticketSelectionError && (
                <Typography
                  variant="text-4"
                  block
                  style={{ color: 'var(--color-warning)' }}
                >
                  {ticketSelectionError}
                </Typography>
              )}
              {ticketModeLocked && hasTicketPriceDelta && (
                <Box className="ticket-price-change-warning">
                  <Typography variant="text-4" translation={null} block>
                    {ticketPriceDelta > 0
                      ? t('Ticket price increased warning', {
                          amount: toCurrencyString(
                            Math.abs(ticketPriceDelta),
                            '€',
                            false,
                            1
                          ),
                        })
                      : t('Ticket price decreased warning', {
                          amount: toCurrencyString(
                            Math.abs(ticketPriceDelta),
                            '€',
                            false,
                            1
                          ),
                        })}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      <Box className="first-step-field">
        <Typography variant="text-3" translation="reservations">
          Reservation Length
        </Typography>
        <ReservationLength
          startBlock={reservation.timeSlot?.block}
          value={reservation.reservationLength ?? reservationLength}
          onChange={(newValue) => handleChange(newValue, 'reservationLength')}
          activityTarget={activityTarget}
          // hidden={true /*settings?.hideResLength || false */}
        />
      </Box>

      {isNew && hasWaitingList && (
        <SwitchBox
          checked={!!reservation.addToWaitinglist}
          label="Place on Waitinglist"
          translation="reservations"
          onChange={(_, checked) =>
            authenticate(
              checked ? 'none' : 'reservations.blockedTime.write',
              () => {
                onReservationChange((old) =>
                  !!old
                    ? {
                        ...old,
                        addToWaitinglist: checked
                          ? { time: reservation?.timeSlot?.time || '' }
                          : null,
                        tables: [],
                      }
                    : null,
                );
              },
              undefined,
              { action: 'override.waitinglistToReservation', category: 'override' },
            )
          }
        />
      )}

      {!lightReservation && (
        <Box className="first-step-field">
          <Typography
            variant="text-3"
            translation="reservations"
            style={{ opacity: !reservation.addToWaitinglist ? 1 : 0.6 }}
          >
            Tables
          </Typography>
          <Box flex style={{ alignItems: 'flex-start', gap: 4 }}>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <TableDropdown
                value={reservation.tables}
                disabled={!!reservation.addToWaitinglist}
                combinations={reservation?.timeSlot?.combinations || null}
                onChange={(newValue) => handleChange(newValue, 'tables')}
              />
            </Box>
            {!reservation.addToWaitinglist && !!reservation.tables?.length && (
              <IconButton
                size="small"
                aria-label="Alle Tische abwählen"
                title="Alle Tische abwählen"
                onClick={() => handleChange([], 'tables')}
                style={{ marginTop: 6 }}
              >
                <Close />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
      {lightReservation && (
        <Box className="second-step-field">
          {addMode ? (
            <TextField
              value={reservation.tableStr || currentTable}
              onChange={(e) => {
                if (setCurrentTable) setCurrentTable(e.target.value);
                handleChange(e.target.value, 'tableStr');
              }}
              fullWidth
              label="Tables"
              labelTranslation="reservations"
            />
          ) : (
            <>
              <Typography variant="text-3" translation="reservations">
                Tables
              </Typography>
              <TableStrDropdown
                value={reservation.tableStr || ''}
                options={tableStrOptions}
                onChange={(val) => handleChange(val, 'tableStr')}
                openAddMode={openAddMode}
              />
            </>
          )}
        </Box>
      )}

      {reservation.walkin && (
        <TextField
          value={reservation.guest?.hostComment || ''}
          onChange={(e) => handleChange(e.target.value, 'hostComment')}
          fullWidth
          style={{ marginTop: 10 }}
          label="Comment (Host)"
          labelTranslation="reservations"
        />
      )}

      {!reservation.new && reservation.guest?.email && (
        <SwitchBox
          checked={!!reservation.sendConfirmationEmail}
          onChange={(_, sendConfirmationEmail) =>
            handleChange(sendConfirmationEmail, 'sendConfirmationEmail')
          }
          label="Bestätigungsemail senden"
          translation="reservations"
        />
      )}
      <SwitchBox
        checked={showTimesInEditMode}
        label="Zeiten an Tischen anzeigen"
        color="primary"
        onChange={(_, checked) => setShowTimesInEdit(checked)}
      />
    </Box>
  );
};

export default FirstStep;
